import {diff} from "deep-object-diff";
import {dot} from "dot-object";
import {reactive, ref, watch} from "vue";
import FormErrors from "./FormErrors";

const convertArraysToObjects = (obj) => _.mapValues(obj, function (value) {
	if (_.isArray(value)) {
		return {};
	}

	if (_.isObject(value)) {
		return convertArraysToObjects(value);
	}

	return value;
});

const flattenKeys = (obj, path = []) =>
	!_.isObject(obj)
		? {[path.join(".")]: obj}
		: _.reduce(obj, (cum, next, key) => _.merge(cum, flattenKeys(next, [...path, key])), {});

const fill = function (data, template) {
	data = JSON.parse(JSON.stringify(data));
	template = JSON.parse(JSON.stringify(template || {}));
	_.each(template, function (templateValue, key) {
		let camelKey = _.camelCase(key);
		let dataValue = data[camelKey] || data[key];
		if (_.isArray(templateValue)) {
			templateValue = templateValue[0];
			if (!dataValue) {
				_.set(template, key, {});
				return;
			}
			_.set(template, key, _.mapValues(dataValue, function (data) {
				return fill(data, templateValue);
			}));
			return;
		}
		if (_.isObject(templateValue)) {
			if (!dataValue) {
				_.set(template, key, templateValue);
				return;
			}
			templateValue = _.isArray(templateValue) ? templateValue[0] : templateValue;
			_.set(template, key, fill(dataValue, templateValue));
			return;
		}
		if (!_.has(data, key) && !_.has(data, camelKey)) {
			_.set(template, key, templateValue);
			return;
		}
		_.set(template, key, dataValue);
	});
	return template;
};

export default class Form {
	constructor(data, httpClient = null, store = null) {
		// this.prepared = false;
		// this.isPrepared = false;
		this.originalData = _.cloneDeep(data);
		this.errors = new FormErrors();
		let errors = this.errors;
		let _data = _.cloneDeep(data);
		_data = convertArraysToObjects(_data);
		this.data = reactive(_data);
		this.httpClient = httpClient;
		this.store = store;
		this.isDirty = false;
		this.processing = false;
		this.isPrepared = true;
		this.validated = false;
		this.delayProcessing = false;
		this.succeeded = null;
		this.watcher = null;
		this.onChangeOnceCallback = null;
		this.onChangeCallback = null;
		this.on422Callback = null;
		this.uniqueName = null;
		// this.watch();
		return this;
	}

	isReady() {
		return this.isPrepared;
	}

	watch() {

		if (!this.isPrepared) {
			console.log("unwatch");
			return this.unwatch();
		}
		console.log("watch");
		if (!this.onChangeCallback && !this.onChangeOnceCallback) {
			return this.unwatch();
		}
		if (this.watcher) {
			return;
		}

		this.watcher = watch(this.data, (newData, oldData) => {
			let diffs = diff(this.originalData, newData);
			if (!Object.keys(diffs).length) {
				this.isDirty = false;
				return;
			}
			this.isDirty = true;
			let data = _.mapValues(dot(diffs), (value, key) => {
				return {
					newValue: value,
					oldValue: _.get(oldData, key),
				};
			});
			if (this.onChangeOnceCallback) {
				this.onChangeOnceCallback({
					form: this,
					newData: newData,
					oldData: oldData,
					diffs: data,
				});
				this.onChangeOnceCallback = null;
			}
			if (this.onChangeCallback) {
				this.onChangeCallback({
					form: this,
					newData: newData,
					oldData: oldData,
					diffs: data,
				});
			}
		}, {
			deep: true,
			immediate: false,
		});

		return this;
	}

	unwatch() {
		this.watcher = null;
		return this;
	}

	prepare(callback) {
		this.unwatch();
		this.isPrepared = false;
		this.isDirty = false;

		if (!callback) {
			return;
		}

		callback.call(null, this);
		return this;
	}

	ready() {
		this.isPrepared = true;
		return this.watch();
	}

	prefill(data, check = true) {
		this.unwatch();
		this.initialized = false;
		this.watcher = this.watcher?.();
		this.data = check ? fill(data, _.cloneDeep(this.originalData)) : data;
		this.data = reactive(this.data);
		this.watch();
		this.initialized = true;
		return this;
	}

	onChange(callback = null) {
		console.log("onChange", callback);
		this.onChangeCallback = callback;
		// if (!this.isPrepared) {
		// 	return this;
		// }
		return this.watch();
	}

	onChangeOnce(callback = null) {
		this.onChangeOnceCallback = callback;
		// if (!this.isPrepared) {
		// 	return this;
		// }
		return this.watch();
	}

	setHttpClient(httpClient) {
		this.httpClient = httpClient;
		return this;
	}

	setStore(store = null) {
		this.store = store;
		return this;
	}

	validate(validate = true) {
		this.set("validate", validate);
		return this;
	}

	continueProcessing(delay = true) {
		this.delayProcessing = delay;
		return this;
	}

	processed() {
		this.processing = false;
		return this;
	}

	delayResponse(delay = 0) {
		this.delay = delay;
		return this;
	}

	get(route, payload = null) {
		return this.submit("get", route, payload);
	}

	post(route, payload = null) {
		return this.submit("post", route, payload);
	}

	patch(route, payload = null) {
		return this.submit("patch", route, payload);
	}

	delete(route, payload = null) {
		return this.submit("delete", route, payload);
	}

	unique(name = null) {
		this.uniqueName = name;
		return this;
	}

	cancel() {
		this.httpClient.cancel(this.uniqueName);
		return this;
	}

	submit(method, route, overridePayload = null) {
		this.uniqueName = this.uniqueName ?? crypto?.randomUUID() ?? Date.now();
		this.processing = true;
		this.succeeded = null;

		let requestedMethod = method;
		let payload = overridePayload || this.data;
		let requestPayload = _.cloneDeep(payload);

		this.errors.clear();

		return new Promise((resolve, reject) => {
			if ("patch" === method) {
				method = "post";
				requestPayload._method = "PATCH";
			}

			if ("delete" === method) {
				method = "post";
				requestPayload._method = "DELETE";
			}

			this.httpClient.unique(this.uniqueName).delayResponse(this.delay)[method](route, requestPayload)
				.then(response => {
					this.isDirty = false;
					this.validated = true;
					this.success(response.data, requestedMethod);
					resolve(response.data);
				})
				.catch(error => {
					this.validated = false;
					this.processed();
					this.fail(error);
					reject(error);
				}).finally(() => {
				this.set("validate", false);
				this.unique();
				if (!this.delayProcessing) {
					this.processed();
				}
			});
		});
	}

	fail(error) {
		this.succeeded = false;

		if (error.response?.status !== 422) {
			throw error;
		}

		this.errors.record(error.response.data.errors);

		if (this.store) {
			this.store.dispatch("alertsStore/formError", this.errors.all());
		}

		if (this.on422Callback) {
			this.on422Callback.call(null, this);
		}
	}

	on422(callback) {
		this.on422Callback = callback;
		return this;
	}

	success(data, method) {
		this.succeeded = true;
	}

	reset(field = null) {
		let data = _.cloneDeep(this.originalData);

		if (!field) {
			this.data = data;
			this.data = ref(this.data);
			this.errors.clear();
			return;
		}

		this.set(field, _.get(data, field));
	}

	resetDefaults() {
		this.originalData = _.cloneDeep(this.data);
	}

	setOptions(data) {
		this.options = data;

		return this;
	}

	set(field, value, index) {
		value = _.cloneDeep(value);

		if (undefined !== index) {
			field = field + "." + index;
		}

		_.set(this.data, field, value);
	}

	fill(data) {
		_.each(data, function (value, key) {
			this.set(key, value);
		}.bind(this));
	}

	setInstances(field, n) {
		let data = _.values(this.getData(field));

		if (!Array.isArray(data)) {
			data = [];
		}

		if (data.length < n) {
			let template = _.get(this.originalData, field)[0];
			while (data.length < n) {
				data.push(_.cloneDeep(template));
			}
		} else {
			data = data.slice(0, Math.max(0, n));
		}

		this.set(field, data);
	}

	setKeys(field, keys, values) {
		values = values || [];

		if (!keys.length) {
			this.set(field, {});
			return;
		}

		let form = this;
		let newData = {};

		_.each(keys, function (key) {
			newData[key] = values[key] || form.getData(field, key);
		});

		form.set(field, newData);
	}

	getData(field = null, index = null) {
		if (!field) {
			return this.data;
		}
		let defaultData = _.cloneDeep(_.get(this.originalData, field, null));
		defaultData = Array.isArray(defaultData) ? defaultData[0] : defaultData;

		if (index !== null) {
			field = field + "." + index;
		}

		return _.cloneDeep(_.get(this.data, field, defaultData));
	}

	getOption(field, index) {
		let defaultData = _.cloneDeep(_.get(this.options, field, null));
		defaultData = Array.isArray(defaultData) ? defaultData[0] : defaultData;

		if (undefined !== index) {
			field = field + "." + index;
		}

		return _.cloneDeep(_.get(this.options, field, defaultData));
	}

	optionsByFieldKeys(options, field, id) {
		options = _.get(this.options, options);
		let keys = _.keys(_.get(this.data, field));
		id = id || "id";

		return _.filter(options, function (option) {
			return _.indexOf(keys, option[id]) > -1;
		});
	}

	optionsByFieldArray(options, field, id) {
		options = _.get(this.options, options);
		let keys = this.getData(field);
		id = id || "id";

		return _.filter(options, function (option) {
			return _.indexOf(keys, option[id]) > -1;
		});
	}

	pushDefault(field, index, before, fallback) {
		let data = _.cloneDeep(_.get(this.originalData, field, _.get(this.originalData, fallback))[0]);
		let values = _.values(this.getData(field));
		before = before || false;
		index = (index && before ? index : index + 1) || values.length;
		values.splice(index, 0, data);
		this.set(field, values);

		return index;
	}

	removeIndex(field, index, preserveKeys = false) {
		let data = this.getData(field);

		_.unset(data, index);

		if (_.isArray(data) && !preserveKeys) {
			data = _.filter(data, function (value) {
				return value;
			});
			data = _.values(data);
		}

		this.set(field, data);
	}

	fieldContainsValue(field, value) {
		let data = this.getData(field);

		return data.indexOf(value) > -1;
	}
}
